import React, { memo, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Popper,
  Paper,
  ClickAwayListener,
  ListItem,
  ListItemButton,
} from '@mui/material';
import {
  MoreVert,
  ExpandMore,
  RadioButtonUnchecked,
  RadioButtonChecked,
  ExpandLess,
} from '@mui/icons-material';
import { fontSize } from '../../..';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  toggleAxesEdited,
  updateAxesQueryParam,
} from '../../../redux/ChartPoperties/ChartPropertiesActions';
import {
  setParameterData,
  setParameterIntoFilters,
} from '../../../redux/Parameters/ParametersAction';
import isNumber from 'lodash/isNumber';
import { chartFilterGroupEdited } from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import { updateCalculation } from '../../../redux/Calculations/CalculationsActions';
import { ParameterProps } from '../ParameterContainer';
import { callQueryAPI } from '../../../redux/ChartPoperties/ChartControlsActions';
import { callQueryAPIDashboard } from '../../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { AxisType } from '../../CommonFunctions/aliases';

const MENU_ITEM_HEIGHT = 30;
const MENU_OVERSCAN_ITEMS = 8;
const MAX_VIRTUAL_SCROLL_HEIGHT = 8_000_000;

interface RadioOptionProps extends ParameterProps {
  maxHeight?: number;
}

const RadioOption = memo(({ option, isSelected, onClick, color, style }: any) => {
  const handleClick = useCallback(() => onClick(option), [option, onClick]);

  return (
    <ListItem disablePadding style={style}>
      <ListItemButton
        onClick={handleClick}
        selected={isSelected}
        sx={{
          py: 0.8,
          px: 1,
          minHeight: 28,
          marginY: '1px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(132, 132, 132, 0.05)',
            '&:hover': {
              backgroundColor: 'rgba(132, 132, 132, 0.05)',
            },
          },
          transition: 'all 0.1s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
          {isSelected ? (
            <RadioButtonChecked
              sx={{
                fontSize: '1rem',
                color: color,
              }}
            />
          ) : (
            <RadioButtonUnchecked
              sx={{
                fontSize: '1rem',
                color: color,
              }}
            />
          )}
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: isSelected ? 500 : 400,
              flex: 1,
              fontSize: fontSize.extraSmall,
              color: color,
              textOverflow: 'ellipsis',
            }}
          >
            {option}
          </Typography>
        </Box>
      </ListItemButton>
    </ListItem>
  );
});

const Radio = ({
  parameter,
  handleEditParameter,
  editMode,
  showDash,
  propKey,
  maxHeight = 270,
  color,
  backgroundColor,
}: RadioOptionProps) => {
  const { chartProperties, calculations } = useSelector((state: any) => {
    return {
      chartProperties: state.chartProperties,
      calculations: state.calculations,
    };
  });
  const dispatch = useDispatch();

  const options = useMemo(() => {
    let opts = [...(parameter.values || [])];

    // If values is empty, generate options from minValue, maxValue, and stepValue
    if (opts.length === 0 && parameter.minValue !== undefined && parameter.maxValue !== undefined) {
      // Handle date type differently
      if (parameter.columns?.dataType === 'date') {
        const minDate = new Date(parameter.minValue);
        const maxDate = new Date(parameter.maxValue);
        const step = Number(parameter.stepValue) || 1; // step in days

        for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + step)) {
          opts.push(new Date(d).toISOString().split('T')[0]); // Format as YYYY-MM-DD
        }
      } else {
        const minValue =
          typeof parameter.minValue === 'string'
            ? parseFloat(parameter.minValue)
            : parameter.minValue;
        const maxValue =
          typeof parameter.maxValue === 'string'
            ? parseFloat(parameter.maxValue)
            : parameter.maxValue;
        const step =
          typeof parameter.stepValue === 'string'
            ? parseFloat(parameter.stepValue)
            : parameter.stepValue || 1;

        for (let i = minValue; i <= maxValue; i += step) {
          opts.push(i);
        }
      }
    }

    return Array.from(new Set(opts));
  }, [
    parameter.values,
    parameter.columns?.defaultValue,
    parameter.columns?.dataType,
    parameter.minValue,
    parameter.maxValue,
    parameter.stepValue,
  ]);

  const [selectedValue, setSelectedValue] = useState<string>(() => {
    const defaultVal = parameter.columns.defaultValue;
    if (parameter.userSelection) {
      const filtered = options.some((val: any) => String(val).includes(parameter.userSelection));
      return filtered ? parameter.userSelection : defaultVal;
    }
    if (defaultVal && options.some((option: any) => option == defaultVal)) {
      return parameter.columns.defaultValue;
    }
    return '';
  });

  // console.log(options);
  // console.log(selectedValue);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [dropdownScrollTop, setDropdownScrollTop] = useState(0);

  const selectBoxRef = useRef<HTMLDivElement>(null);
  const currentChartAxesRef = useRef<any[]>([]);
  const currentChatFieldRef = useRef<any>(null);
  const currentChartAxesNameRef = useRef<any>(null);
  const itemIndexRef = useRef<any>(null);
  const isFirstRender = useRef(true);

  const currentChartField = chartProperties.properties[propKey]?.chartAxes[0]?.fields?.find(
    (field: any) => field.parameterId === parameter.parameterId,
  );

  const callQueryAPIFilter = () => {
    let callAPIDashboard = false;
    chartProperties.propList[1].forEach((tabTileId: string) => {
      chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
        if (field.parameterId === parameter.parameterId) {
          dispatch(callQueryAPI(tabTileId, true));
          callAPIDashboard = true;
        }
      });
      let measureAxes = chartProperties.properties[tabTileId].chartAxes.filter((axis: any) =>
        [AxisType.Measure, AxisType.X, AxisType.Y].includes(axis.name),
      );
      measureAxes.forEach((axes: any) => {
        axes.fields.some((field: any) => {
          if (field.override) {
            field.override[0].fields.some((FilterField: any) => {
              if (FilterField.parameterId === parameter.parameterId) {
                dispatch(callQueryAPI(tabTileId, true));
                callAPIDashboard = true;
              }
            });
          }
        });
      });
    });
    if (showDash && callAPIDashboard) {
      dispatch(callQueryAPIDashboard(true));
    }
  };

  const updateUserFilterItem = (
    propKey: string,
    bIndex: number,
    itemIndex: number,
    field: any,
    axesName: string,
  ) => {
    dispatch(updateAxesQueryParam(propKey, bIndex, itemIndex, field, axesName));
  };

  const callAPI = (uid: string | undefined) => {
    let callAPIDashboard = false;
    Object.entries(chartProperties.properties).forEach(([tabTileId, prop]: [string, any]) => {
      prop.chartAxes?.forEach((axis: any) => {
        axis.fields?.forEach((field: any) => {
          if (field.SavedCalculationUUID === uid) {
            dispatch(callQueryAPI(tabTileId, true));
            callAPIDashboard = true;
          }
        });
      });
    });
    if (showDash && callAPIDashboard) {
      dispatch(callQueryAPIDashboard(true));
    }
  };

  const handleToggle = useCallback(() => setIsDropdownOpen((prev) => !prev), []);
  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
    setDropdownScrollTop(0);
  }, []);

  const handleDropdownScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setDropdownScrollTop(event.currentTarget.scrollTop);
  }, []);

  const visibleItemsCount = Math.max(1, Math.floor(maxHeight / MENU_ITEM_HEIGHT));
  const totalContentHeight = options.length * MENU_ITEM_HEIGHT;
  const scaledScrollHeight = Math.min(totalContentHeight, MAX_VIRTUAL_SCROLL_HEIGHT);
  const maxTopIndex = Math.max(0, options.length - visibleItemsCount);

  const visibleStartIndex = useMemo(() => {
    if (options.length === 0) return 0;

    const rawIndex =
      totalContentHeight <= MAX_VIRTUAL_SCROLL_HEIGHT
        ? Math.floor(dropdownScrollTop / MENU_ITEM_HEIGHT)
        : Math.floor(
            (dropdownScrollTop / Math.max(1, scaledScrollHeight - maxHeight)) * maxTopIndex,
          );

    return Math.max(0, rawIndex - MENU_OVERSCAN_ITEMS);
  }, [
    dropdownScrollTop,
    maxHeight,
    maxTopIndex,
    options.length,
    scaledScrollHeight,
    totalContentHeight,
  ]);

  const visibleEndIndex = Math.min(
    options.length,
    visibleStartIndex + visibleItemsCount + MENU_OVERSCAN_ITEMS * 2,
  );

  const visibleOptions = options.slice(visibleStartIndex, visibleEndIndex);

  const handleSelect = (value: string) => {
    setSelectedValue(value);

    dispatch(setParameterData(parameter.parameterId, value));
    // if (currentChatFieldRef.current && currentChatFieldRef.current.isParameterized) {
    //   if (currentChatFieldRef.current.fieldtypeoption === 'Pick List') {
    //     currentChatFieldRef.current.userSelection = [value];
    //   } else currentChatFieldRef.current.exprInput = value;

    //   updateUserFilterItem(
    //     propKey,
    //     0,
    //     itemIndexRef.current,
    //     currentChatFieldRef.current,
    //     currentChartAxesNameRef.current,
    //   );
    // }
    Array.isArray(parameter.chartFilter) &&
      parameter.chartFilter.forEach((cf: any) => {
        if (!cf.currentChartAxesName?.includes('chartAxes_')) {
          if (cf.propKey) {
            const clonedField = {
              ...chartProperties.properties[cf.propKey].chartAxes[0].fields[cf.itemIndex],
            };
            if (clonedField.fieldtypeoption === 'Pick List') {
              clonedField.userSelection = [value];
            } else clonedField.exprInput = value;
            updateUserFilterItem(cf.propKey, 0, cf.itemIndex, clonedField, cf.currentChartAxesName);
          } else if (cf.itemIndex >= 0 && cf.currentChartAxesName) {
            chartProperties.propList[1].forEach((tabTileId: string) => {
              chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
                if (field.parameterId === parameter.parameterId) {
                  dispatch(
                    setParameterIntoFilters(
                      tabTileId,
                      parameter.parameterId,
                      field.uId,
                      cf.itemIndex,
                      cf.currentChartAxesName,
                    ),
                  );

                  const clonedField = {
                    ...chartProperties.properties[tabTileId].chartAxes[0].fields[cf.itemIndex],
                  };
                  if (clonedField.fieldtypeoption === 'Pick List') {
                    clonedField.userSelection = [value];
                  } else clonedField.exprInput = value;
                  updateUserFilterItem(
                    tabTileId,
                    0,
                    cf.itemIndex,
                    clonedField,
                    cf.currentChartAxesName,
                  );
                }
              });
            });
          }
        }
      }),
      callQueryAPIFilter();

    if (parameter.calculationId.length > 0) {
      const targetCalculation = calculations.savedCalculations.filter(
        (cal: any) => cal.uuid === parameter.calculationId.find((id: any) => id === cal.uuid),
      );
      targetCalculation.map((calculation: any) => {
        const targetFlowNames = Object.keys(calculation.calculationInfo.flows);
        targetFlowNames.map((flowName: any) => {
          const targetFlow = calculation.calculationInfo.flows[flowName][0];
          const conditionFilters = calculation.calculationInfo.conditionFilters;
          if (targetFlow.flow === 'IfElse') {
            for (const conditionFilter in conditionFilters) {
              let newConditions = conditionFilters[conditionFilter][0];
              newConditions.conditions.forEach((filter: any) => {
                if (filter.parameterId.length > 0) {
                  filter.parameterId.map((parameterId: string, index: number) => {
                    if (parameterId === parameter.parameterId) {
                      if (!selectedValue) filter.rightOperand[index] = '0';
                      else filter.rightOperand[index] = selectedValue.toString();
                    }
                  });
                }
              });
            }
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          } else {
            let source = targetFlow.source;
            let input;
            if (isNumber(value) && value <= 0) {
              if (targetFlow.flow === 'division') input = 1;
              else input = 0;
            } else {
              input = value;
            }
            source = source.map((source: any, index: number) => {
              const isParameterized = targetFlow.parameter.find(
                (param: any) =>
                  param.parameterId === parameter.parameterId && param.index === index,
              );
              if (isParameterized) {
                return input;
              }
              return source;
            });
            targetFlow.source = source;
            calculation.calculationInfo.flows[flowName][0] = targetFlow;
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          }
          callAPI(calculation.uuid);
        });
      });
    }

    parameter?.chartFilter?.forEach((filter: any) => {
      if (filter?.currentChartAxesName?.includes('chartAxes_')) {
        const currentChartAxesName_ = filter?.currentChartAxesName.split('_')[1];
        if (filter.propKey) {
          const chartAxes = chartProperties.properties[filter.propKey]?.chartAxes;
          const chartFilter = chartAxes[chartAxes.length - 1].fields;
          chartFilter?.forEach((field: any, index: number) => {
            if (currentChartAxesName_ === field.enableOverrideForUID) {
              return;
            }
            if (field.uId === currentChartAxesName_) {
              const override_ = field.override;
              if (Array.isArray(override_) && override_.length > 0) {
                override_[0].fields.forEach((field_: any, index_: number) => {
                  if (field_.uId === filter.chartFilterId) {
                    if (field_.fieldtypeoption === 'Pick List') {
                      field_.userSelection = [value];
                    } else field_.exprInput = value;
                    field.override = override_;
                    updateUserFilterItem(
                      filter.propKey,
                      chartAxes.length - 1,
                      index,
                      field,
                      'chartAxes',
                    );
                    callQueryAPIFilter();
                  }
                });
              }
            }
          });
        } else {
        }
      }
    });
    handleDropdownClose();
  };

  const selectedOption = useMemo(
    () => parameter.values?.find((option: any) => option === selectedValue) || selectedValue,
    [parameter.values, selectedValue],
  );

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    currentChartAxesRef.current = chartProperties.properties[propKey]?.chartAxes[0]?.fields || [];

    // Find the field where any chartFilter in parameter.chartFilter matches filter.uId
    currentChatFieldRef.current = currentChartAxesRef.current.find(
      (filter: any) =>
        Array.isArray(parameter.chartFilter) &&
        parameter.chartFilter.some((cf: any) => {
          if (cf.chartFilterId === filter.uId) {
            currentChartAxesNameRef.current = cf.currentChartAxesName;
            itemIndexRef.current = cf.itemIndex;
            return cf.chartFilterId === filter.uId;
          }
        }),
    );
  }, [
    chartProperties.properties[propKey]?.chartAxes[0]?.fields,
    parameter.chartFilter,
    propKey,
    currentChartField?.parameterId,
  ]);

  // if the parameter had any default value then it will update the Graph
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (
      currentChatFieldRef.current &&
      currentChatFieldRef.current.isParameterized &&
      selectedValue
    ) {
      if (currentChatFieldRef.current.fieldtypeoption === 'Pick List') {
        currentChatFieldRef.current.userSelection = [selectedValue];
      } else currentChatFieldRef.current.exprInput = selectedValue;

      updateUserFilterItem(
        propKey,
        0,
        itemIndexRef.current,
        currentChatFieldRef.current,
        currentChartAxesNameRef.current,
      );
      callQueryAPIFilter();
    }

    if (parameter.calculationId.length > 0) {
      const targetCalculation = calculations.savedCalculations.filter(
        (cal: any) => cal.uuid === parameter.calculationId.find((id: any) => id === cal.uuid),
      );
      targetCalculation.map((calculation: any) => {
        const targetFlowNames = Object.keys(calculation.calculationInfo.flows);
        targetFlowNames.map((flowName: any) => {
          const targetFlow = calculation.calculationInfo.flows[flowName][0];
          const conditionFilters = calculation.calculationInfo.conditionFilters;
          if (targetFlow.flow === 'IfElse') {
            for (const conditionFilter in conditionFilters) {
              let newConditions = conditionFilters[conditionFilter][0];
              newConditions.conditions.forEach((filter: any) => {
                if (filter.parameterId.length > 0) {
                  filter.parameterId.map((parameterId: string, index: number) => {
                    if (parameterId === parameter.parameterId) {
                      if (!selectedValue) filter.rightOperand[index] = '0';
                      else filter.rightOperand[index] = selectedValue.toString();
                    }
                  });
                }
              });
            }
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          } else {
            let source = targetFlow.source;
            let input;
            if (isNumber(selectedValue) && selectedValue <= 0) {
              if (targetFlow.flow === 'division') input = 1;
              else input = 0;
            } else {
              input = selectedValue;
            }
            source = source.map((source: any, index: number) => {
              const isParameterized = targetFlow.parameter.find(
                (param: any) =>
                  param.parameterId === parameter.parameterId && param.index === index,
              );
              if (isParameterized) {
                return input;
              }
              return source;
            });
            targetFlow.source = source;
            calculation.calculationInfo.flows[flowName][0] = targetFlow;
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          }
        });
      });
    }

    parameter?.chartFilter?.forEach((filter: any) => {
      if (filter?.currentChartAxesName?.includes('chartAxes_')) {
        const currentChartAxesName_ = filter?.currentChartAxesName.split('_')[1];
        const chartAxes = chartProperties.properties[propKey]?.chartAxes;
        const chartFilter = chartAxes[chartAxes.length - 1].fields;
        chartFilter?.forEach((field: any, index: number) => {
          if (currentChartAxesName_ === field.enableOverrideForUID) {
            return;
          }
          if (field.uId === currentChartAxesName_) {
            const override_ = field.override;
            if (Array.isArray(override_) && override_.length > 0) {
              override_[0].fields.forEach((field_: any, index_: number) => {
                if (field_.uId === filter.chartFilterId) {
                  if (field_.fieldtypeoption === 'Pick List') {
                    field_.userSelection = [selectedValue];
                  } else field_.exprInput = selectedValue;
                  field.override = override_;
                  updateUserFilterItem(propKey, chartAxes.length - 1, index, field, 'chartAxes');
                  callQueryAPIFilter();
                }
              });
            }
          }
        });
      }
    });
  }, [
    parameter.chartFilter,
    // currentChatFieldRef.current,
    // propKey
  ]);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              transform: 'capitalize',
              color: color,
              textAlign: 'start',
            }}
          >
            {parameter?.name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: -1,
            transition: 'all 0.3s ease-in-out',
          }}
        >
          {editMode && (
            <IconButton
              size="small"
              id="basic-button"
              aria-controls={Boolean(anchorEl) ? 'basic-menu' : undefined}
              sx={{ color: color }}
              onClick={handleMenuClick}
            >
              <MoreVert />
            </IconButton>
          )}
          <IconButton
            size="small"
            sx={{
              mr: '0.4rem',
              color: color,
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem dense onClick={() => handleEditParameter(parameter.parameterId)}>
          Remove
        </MenuItem>
      </Menu>

      {expanded && (
        <FormControl fullWidth size="small">
          <Box
            ref={selectBoxRef}
            onClick={handleToggle}
            sx={{
              border: '1px solid',
              borderRadius: '4px',
              padding: '0.25rem 0.5rem',
              paddingRight: '0.3rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              backgroundColor: backgroundColor,
              color: color,
              textAlign: 'left',
              transition: 'all 0.2s ease',
              borderColor: isDropdownOpen ? '#1c1c1c' : color || 'rgba(110, 110, 110, 0.5)',
              // borderColor: ' #6e6e6e',
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid',
                borderColor: color || 'rgba(110, 110, 110, 0.5)',
                // borderColor: ' #6e6e6e',
              },
              '&:hover': {
                borderColor: color || '#1c1c1c',
              },
            }}
            tabIndex={0}
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
            aria-label={`Select ${parameter.name}`}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                flex: 1,
                color: color,
                fontSize: fontSize.extraSmall,
              }}
            >
              {selectedOption || 'Select an option...'}
            </Typography>

            <ExpandMore
              sx={{
                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                color: color,
                transition: 'transform 0.2s ease-in-out',
                mr: '0.4rem',
              }}
            />
          </Box>

          <Popper
            open={isDropdownOpen}
            anchorEl={selectBoxRef.current}
            style={{ width: selectBoxRef.current?.offsetWidth! + 10 }}
            placement="bottom"
          >
            <Paper
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                margin: '0 5px 0 5px',
                backgroundColor: 'white',
              }}
            >
              <ClickAwayListener onClickAway={handleDropdownClose}>
                <Box>
                  <Box
                    sx={{ maxHeight, overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}
                    onScroll={handleDropdownScroll}
                  >
                    <Box sx={{ height: scaledScrollHeight, position: 'relative' }}>
                      <Box sx={{ position: 'sticky', top: 0, left: 0, right: 0 }}>
                        {visibleOptions.map((option: any, index: number) => (
                          <RadioOption
                            key={`${visibleStartIndex + index}-${option}`}
                            option={option}
                            isSelected={selectedValue === option}
                            onClick={handleSelect}
                            color={color}
                            style={{ height: MENU_ITEM_HEIGHT }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Popper>
        </FormControl>
      )}
    </Box>
  );
};

export default memo(Radio);
